import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppContext } from '../../context/AppContext';
import { useState, useEffect, useMemo } from 'react';
import Modal from '../helpers/Modal';
import { buildKonfigearDesignLink, getDesignDisplaySpecs } from '../../utils/buildKonfigearDesignLink';
import { ECO_BEAM_PRODUCT_TYPE } from '../../utils/ecoBeamPricing';
import { EcoBeamPricingTable } from '../helpers/EcoBeamPricingTable';
const SOLAR_PRO_COMPANY_TYPES = [
    'Engineering Firm',
    'Installer',
    'General Contractor',
    'Subcontractor',
    'Installer / Solar Pro',
    'Electrical / General Contractor',
    'EPC / Developer',
];

export const RenderDesigns = ({ design }) => {
    const { user, company } = useAppContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [link, setLink] = useState("https://app.konfigear.com/mtsolar/");
    // const render_link = "https://app.konfigear.com/mtsolar/?quote_id=139%2520Rocktown%2520&user_id=8369&user_email=kosgood112%2540gmail.com&sku_code=1P-0-6TOP-XD-84-L-5Hx3W-41BK&duty_size=XD&pole_size=6in%2520Pipe%2520Sch%252040&product_type=Beam&number_of_poles=1&pole_spacing=0&pole_height=6.63&wing_span=84&max_foundation_depth=5.75&tilt_angle=10&cantilever=258&ground_clearance=5&no_poles=1&module_area=426.10104166666673&array_width=22.675&array_height=18.791666666666668&rail_length=225.5&module_length=89.7&passed_overhang_check=true&module_width_gap=0.5&module_length_gap=1&total_frame_length=21.5&ground_to_top=8.263138671991065&projected_panel_height=3.263138671991065&wind_speed_override=null&snow_load_override=null&direct_snow_load=false&add_angle_brace=false&designer_name=Kiyle%2520Osgood&designer_email=kosgood112%2540gmail.com&designer_phone=6093062946&project_id=139%2520Rocktown%2520&site_address=139%2520Rocktown%2520Lambertville%2520Rd%252C%2520Lambertville%252C%2520NJ%252008530%252C%2520USA&module_info=SS8-72HD-580N&module_width=44.6&number_rows=5&number_columns=3&risk_category=I&exposure_category=C&frame_duty_override=auto&pole_override=auto&soil_type=sand&customer_foundation_override=48_Square&foundation_type=Square&foundation_size=48&check_rails=false&total_price=6293.18&total_weight=1231.77&total_pallets=1&pdf=https%3A%2F%2Fskycivquotes.s3.amazonaws.com%2Fe5555201-2d84-4acc-8387-96d4218ec4fb.pdf&id=12424"

    // Helper function to format numbers with commas
    const formatPrice = (price) => {
        return price ? price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
    };

    const productType = design.product_type ?? design.beam_series;
    const isEcoBeam = productType === ECO_BEAM_PRODUCT_TYPE;
    const showPricing = !design.is_custom && (
        user?.user_type === 'Internal' ||
        (user?.user_type === 'Standard' && (
            company?.company_type === 'Property Owner' ||
            SOLAR_PRO_COMPANY_TYPES.includes(company?.company_type)
        ))
    );

    const specs = useMemo(() => getDesignDisplaySpecs(design), [design]);
    const canRenderModel = Boolean(
        design?.sky_civ_details?.config ||
        design?.intake
    );

    useEffect(() => {
        if (!canRenderModel) return;
        setLink(buildKonfigearDesignLink(design, user));
    }, [design, user, canRenderModel]);

    useEffect(() => {
        if (isModalOpen) {
            console.log('Rendering design:', design.id);
        }
    }, [design, isModalOpen])

    const specItems = [
        {
            show: specs.ground_clearance != null,
            label: 'Ground clearance',
            value: `${specs.ground_clearance} ft`,
        },
        {
            show: specs.tilt_angle != null,
            label: 'Tilt angle',
            value: `${specs.tilt_angle}°`,
        },
        {
            show: specs.number_rows != null,
            label: 'Rows',
            value: specs.number_rows,
        },
        {
            show: specs.number_columns != null,
            label: 'Columns',
            value: specs.number_columns,
        },
        {
            show: specs.module_width != null && specs.module_length != null,
            label: 'Module size',
            value: `${specs.module_width} × ${specs.module_length} in`,
        },
        {
            show: specs.exposure_category != null,
            label: 'Wind exposure',
            value: specs.exposure_category,
        },
        {
            show: specs.frame_duty_override != null,
            label: 'Frame duty',
            value: specs.frame_duty_override,
        },
        {
            show: specs.pole_spacing_override != null,
            label: 'Pole size',
            value: specs.pole_spacing_override,
        },
        {
            show: specs.foundation_type != null,
            label: 'Foundation',
            value: `${specs.foundation_type}${specs.foundation_size != null ? ` ${specs.foundation_size} in` : ''}`,
        },
        {
            show: specs.max_foundation_depth != null,
            label: 'Foundation depth',
            value: specs.max_foundation_depth,
        },
    ].filter((item) => item.show);

    const specColumnSplit = Math.ceil(specItems.length / 2);
    const specColumns = [
        specItems.slice(0, specColumnSplit),
        specItems.slice(specColumnSplit),
    ];

    const renderDesigns = (
        <div className="render-overlay-container">
            <iframe src={link} title="3D Design Model" />
            <div className="render-overlay">
                <div className="render-overlay-panel render-overlay-details">
                    <h4>Design Details</h4><hr />
                    <p><strong>Name:</strong> {design.design_name || 'N/A'}</p>
                    <p><strong>SKU:</strong> {design.unique_id || 'N/A'}</p>
                    <p><strong>Created by:</strong> {design.created_by_email || 'N/A'}</p>
                    {showPricing && isEcoBeam && <EcoBeamPricingTable compact />}
                    {showPricing && !isEcoBeam && user?.user_type === 'Internal' && (
                        <>
                            <p><strong>Price:</strong> {design.total_design_price ? `$${formatPrice(design.total_design_price)}` : 'NEW'}</p>
                            <p><strong>Solar Pro Price:</strong> {design.total_design_price ? `$${formatPrice(design.total_design_price * 0.7)}` : 'NEW'}</p>
                        </>
                    )}
                    {showPricing && !isEcoBeam && user?.user_type === 'Standard' && (
                        <>
                            {company?.company_type === 'Property Owner' && (
                                <p><strong>MSRP Price:</strong> {design.total_design_price ? `$${formatPrice(design.total_design_price)}` : 'NEW'}</p>
                            )}
                            {SOLAR_PRO_COMPANY_TYPES.includes(company?.company_type) && (
                                <p><strong>Solar Pro Price:</strong> {design.total_design_price ? `$${formatPrice(design.total_design_price * 0.7)}` : 'NEW'}</p>
                            )}
                        </>
                    )}
                    {!isEcoBeam && (
                        <p><strong>Weight:</strong> {design.total_design_weight ? `${design.total_design_weight.toFixed(2)} lbs` : 'NEW'}</p>
                    )}
                </div>
                {specItems.length > 0 && (
                    <div className="render-overlay-panel render-overlay-specs">
                        <h4>Specifications</h4><hr />
                        <div className="render-specs-columns">
                            {specColumns.map((column, columnIndex) => (
                                <div key={columnIndex} className="render-specs-column">
                                    {column.map((item) => (
                                        <p key={item.label}>
                                            <strong>{item.label}:</strong> {item.value}
                                        </p>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )

    

    return (
        <>
            <button 
                onClick={() => setIsModalOpen(true)} 
                className="btn btn-action btn-secondary"
                title="View 3D Design Model"
                disabled={!canRenderModel}
            >
                <FontAwesomeIcon icon={faExternalLinkAlt} />
                <span>View</span>
            </button>
            {isModalOpen && (
                <Modal form={renderDesigns} setIsModalOpen={setIsModalOpen} isWide={true} />
            )}
        </>
    );
};